import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  Tooltip,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { StationData } from "../types";
import { InfoTooltip } from "./InfoTooltip";

const COLORS = ["primary", "success", "info", "warning"] as const;

interface StationBreakdownChartProps {
  data: StationData[];
}

export const StationBreakdownChart: React.FC<StationBreakdownChartProps> = ({
  data,
}) => {
  const theme = useTheme();
  const maxCount = data.length > 0 ? Math.max(...data.map((d) => d.reportCount)) : 0;
  const totalReports = data.reduce((sum, d) => sum + d.reportCount, 0);

  if (data.length === 0) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Reports by Station
            </Typography>
            <InfoTooltip title="Breakdown of completed reports by station. Sorted by report count descending. Station names are resolved from the user management service." />
          </Box>
        }
        subheader={`${totalReports} total reports across ${data.length} station${data.length !== 1 ? "s" : ""}`}
      />
      <CardContent>
        <Stack spacing={2}>
          {data.map((d, idx) => {
            const color = COLORS[idx % COLORS.length];
            const pct = totalReports > 0 ? ((d.reportCount / totalReports) * 100).toFixed(1) : "0";
            return (
              <Tooltip
                key={d.stationId}
                title={`${pct}% of total reports`}
                arrow
                placement="left"
              >
                <Box sx={{ cursor: "pointer" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {d.stationName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                      color={`${color}.main`}
                    >
                      {d.reportCount} reports
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={maxCount > 0 ? (d.reportCount / maxCount) * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette[color].main, 0.1),
                      "& .MuiLinearProgress-bar": {
                        bgcolor: `${color}.main`,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};
